import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { addToast, Button, TextInput } from '@octano/global-ui';
import React, { useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Card, CardHeader, Col, Form, Row } from 'reactstrap';
import GoBackButton from '../../../../components/buttons/GoBackButton';
import { PathsLayouts } from '../../../../config/routes';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { createEvaluationScale, updateEvaluationScale } from '../api';
import { useSurveyEvaluationScaleLoader } from '../Loaders/SurveyEvaluationScaleLoader';
import { EvaluationScaleOption } from '../types';
import SortableOption from './SortableOption';

export interface FormValues {
  title: string;
  scaleOptions: EvaluationScaleOption[];
}

const EvaluationScaleForm: React.FC = () => {
  const { view } = useParams<{ view: string }>();
  const canSave = useMemo(() => view === 'update' || view === 'create', [view]);
  const history = useHistory();
  const { msgValidations } = useValidations();
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_SURVEY_EVALUATION_SCALES,
  );
  const [loading, setLoading] = useState(false);
  const { data } = useSurveyEvaluationScaleLoader();

  const initialOptions = useMemo(() => {
    if (data?.scaleOptions?.length) {
      return data.scaleOptions;
    }
    return Array.from(
      { length: 6 },
      (_, i) =>
        ({
          optionNumber: i,
          description: t(`scaleCreation.defaultOptions.option${i + 1}`),
        } as EvaluationScaleOption),
    );
  }, [data?.scaleOptions, t]);

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { title: data?.title || '', scaleOptions: initialOptions },
  });
  const { fields: options, append, remove, move } = useFieldArray({
    control,
    name: 'scaleOptions',
    keyName: 'fieldId',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex((o) => o.fieldId === active.id);
      const newIndex = options.findIndex((o) => o.fieldId === over.id);
      move(oldIndex, newIndex);
    }
  };

  const handleCancel = () => history.push(PathsLayouts.surveyEvaluationScales);
  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    const payload = {
      title: values.title,
      scaleOptions: values.scaleOptions.map((opt, idx) => ({
        // el orden parte en 1
        optionNumber: idx + 1,
        description: opt.description,
      })),
    };
    try {
      let response;
      let successMessage;
      if (data?.id) {
        response = await updateEvaluationScale(data?.id, payload);
        successMessage = t('scaleCreation.updateSuccess');
      } else {
        response = await createEvaluationScale(payload);
        successMessage = t('scaleCreation.createSuccess');
      }
      if (response.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('scaleCreation.createError'),
        });
        setLoading(false);
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: successMessage,
      });
      setLoading(false);
    } catch (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('scaleCreation.createError'),
      });
      setLoading(false);
    }
    history.push(`${PathsLayouts.surveyEvaluationScales}`);
  };

  return (
    <Card className="m-3">
      <CardHeader className="bg-white border-0 pt-5">
        <Col className="mb-4">
          <GoBackButton
            onClick={handleCancel}
            text={t('scaleCreation.goBackButton')}
          />
        </Col>
        <p className="mb-0 fs-20 font-weight-bold text-primary">
          {t('scaleCreation.header.title')}
        </p>
        <p className="mb-0 text-muted fs-16">
          {t('scaleCreation.header.subTitle')}
        </p>
      </CardHeader>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="px-4">
          <Col xs={12} md={9}>
            <div style={{ marginRight: 90 }}>
              <TextInput
                name="title"
                control={control}
                label={t('scaleCreation.formFields.title.label')}
                placeholder={t('scaleCreation.formFields.title.placeholder')}
                rules={{ required: msgValidations.required }}
              />
            </div>
          </Col>
        </Row>

        <Row className="px-4 mt-4">
          <Col xs={12} md={9}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={options.map((o) => o.fieldId)}
                strategy={verticalListSortingStrategy}
              >
                {options.map((field, index) => (
                  <SortableOption
                    key={field.fieldId}
                    label={t('scaleCreation.formFields.levelLabel.label')}
                    field={field}
                    index={index}
                    control={control}
                    remove={remove}
                    msgValidations={msgValidations}
                    disabled={options.length <= 2 || loading}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <div
              className="mt-3 mb-4"
              style={{ marginLeft: 55, marginRight: 90 }}
            >
              <Button
                outlined
                fullwidth
                text={t('scaleCreation.actions.addOption')}
                onClick={() =>
                  append({ optionNumber: options.length, description: '' })
                }
                disabled={options.length >= 10}
              />
            </div>
          </Col>
        </Row>
        {canSave ? (
          <Row className="p-4">
            <Col xs={12} className="d-flex justify-content-end mt-4">
              <Button
                outlined
                className="mr-4"
                color="danger"
                text={t('scaleCreation.actions.cancel')}
                onClick={handleCancel}
                style={{ width: '100%', maxWidth: 208 }}
              />
              <Button
                color="secondary"
                text={t('scaleCreation.actions.publish')}
                type="submit"
                loading={loading}
                style={{ width: '100%', maxWidth: 208 }}
              />
            </Col>
          </Row>
        ) : null}
      </Form>
    </Card>
  );
};

export default EvaluationScaleForm;
